import React, { useCallback, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import validate from 'src/utils/validate'
import isEmail from 'src/utils/validators/isEmail'
import { Alert } from 'src/components/Alert'
import { StyledLink } from 'src/components/Link'
import { Button } from 'src/ui/Button'
import { TextField } from 'src/components/TextField'
import { Card } from 'src/ui/Card'
import { AuthPageWrapper, PageContentWrapper } from 'src/pages/PageTemplate'
import { useAuth } from 'src/modules/auth'
import styled from 'styled-components/macro'
import { TermsAndPrivacy } from 'src/ui/TermsAndPrivacy'
import { InvitationMessage } from 'src/ui'

const initialValues = {
  email: '',
}

const getSubmitError = errorCode => {
  switch (errorCode) {
    case 404: {
      return {
        title: 'There is no account with this email',
        description: '',
      }
    }

    default: {
      return {
        title: 'Something went wrong',
        description: '',
      }
    }
  }
}

export const ReConfirm = () => {
  const { isLoading, requestReConfirm } = useAuth()
  const [isSuccess, setIsSuccess] = useState(false)
  const onValidate = useCallback(values => {
    const errors = {}

    errors.email = validate(values.email, isEmail)

    return errors
  }, [])

  const onSubmit = useCallback(
    async values => {
      try {
        const res = await requestReConfirm(values.email)

        if (res?.json?.error?.parsedValidations) {
          return res.json.error.parsedValidations
        }
        if (!res.ok) {
          return { error: res.status }
        }

        setIsSuccess(true)
      } catch (error) {
        return { error: t('Something went wrong') }
      }

      return false
    },
    [requestReConfirm]
  )

  return (
    <AuthPageWrapper>
      <PageContentWrapper style={{ textAlign: 'center' }}>
        <InvitationMessage />
        <Card
          width="500px"
          style={{
            textAlign: isSuccess ? 'center' : 'left',
          }}
        >
          {isSuccess ? (
            <SuccessMessage>
              {t('The confirmation link was sent, please check your email')}

              <StyledLink
                to="/sign-in"
                style={{
                  marginTop: '20px',
                  display: 'block',
                  fontSize: 'inherit',
                  textAlign: 'center',
                }}
              >
                {t('Go to Sign-In page')}
              </StyledLink>
              <TermsAndPrivacy />
            </SuccessMessage>
          ) : (
            <Form
              onSubmit={onSubmit}
              validate={onValidate}
              initialValues={initialValues}
              render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    maxLength={255}
                  />

                  <FormSpy
                    subscription={{
                      submitting: true,
                      pristine: true,
                      valid: true,
                      dirtySinceLastSubmit: true,
                      hasValidationErrors: true,
                      submitErrors: true,
                      error: true,
                      errors: true,
                      submitFailed: true,
                    }}
                  >
                    {({
                      valid,
                      submitFailed,
                      dirtySinceLastSubmit,
                      hasValidationErrors,
                      submitErrors,
                    }) => {
                      const submitError = getSubmitError(submitErrors?.error)

                      return (
                        <>
                          <div mt={4}>
                            {!valid && submitFailed && (
                              <Alert
                                type="error"
                                title={t(submitError.title)}
                                description={t(submitError.description)}
                              />
                            )}
                          </div>
                          <div mt={4}>
                            <Button
                              color="primary"
                              width="100%"
                              bold
                              size="lg"
                              disabled={
                                hasValidationErrors ||
                                (submitFailed && !dirtySinceLastSubmit) ||
                                isLoading
                              }
                              loading={isLoading}
                              onClick={handleSubmit}
                            >
                              {t('Request confirmation link')}
                            </Button>
                          </div>
                        </>
                      )
                    }}
                  </FormSpy>

                  <StyledLink
                    to="/sign-in"
                    style={{
                      marginTop: 20,
                      width: '100%',
                      textAlign: 'right',
                    }}
                  >
                    {t('Go to Sign-In page')}
                  </StyledLink>
                  <TermsAndPrivacy />
                </form>
              )}
            />
          )}
        </Card>
      </PageContentWrapper>
    </AuthPageWrapper>
  )
}

const SuccessMessage = styled.div``
